/**
 * x-cart-upsell-progress directive.
 *
 * Drives the free-gift progress bar in the cart: lets the shopper pick up to
 * `limit` free-gift variants from a swatch list, adds/removes them via the
 * ajax cart, and keeps its selection state in sync with the cart's actual
 * line items (including changes made outside this widget, e.g. the cart
 * drawer or another tab).
 *
 * @options
 *   limit - Maximum number of free-gift variants that can be selected at
 *           once (default: 1).
 *
 * @example
 *   <div x-data x-cart-upsell-progress="{ limit: 1 }">…</div>
 */
(() => {
  const root = (Alpine, el, { limit = 1 }) => {
    Alpine.bind(el, () => ({
      /** Resyncs selection state once the ajax cart has finished loading. */
      ["@cart-init.document"]() {
        this.syncItems();
      },

      /** Morphs in the server-rendered progress bar, then resyncs selection state. */
      ["@cart-request-end.document"](event) {
        this.morphFromSections(event.detail?.requestState);
        this.syncItems();
      },

      ["x-data"]() {
        return {
          items: [],
          isMaxSelection: false,

          init() {
            this.$watch("items", () => {
              this.isMaxSelection = this.items.length >= limit;
            });

            // Disables the remaining swatches once the selection limit is
            // hit, so shoppers can't select more free gifts than allowed.
            this.$watch("isMaxSelection", () => {
              this.updateDisabledSwatches();
            });
          },

          /**
           * Applies/clears `aria-disabled` on the free-gift swatches based
           * on `isMaxSelection`. Called from the `isMaxSelection` watcher,
           * but also explicitly after `syncItems()`: `morphFromSections`
           * replaces the swatch markup with a fresh server render (which
           * never carries `aria-disabled`), and since that doesn't change
           * `isMaxSelection`'s value, the watcher above wouldn't otherwise
           * fire to restore it.
           */
          updateDisabledSwatches() {
            if (this.isMaxSelection) {
              const ids = this.items.map((item) => item.id);

              this.$root
                .querySelectorAll("[data-variant-id]")
                .forEach((node) => {
                  const id = parseInt(node.dataset.variantId, 10);

                  if (!ids.includes(id)) {
                    node.setAttribute("aria-disabled", true);
                  }
                });
            } else {
              this.$root
                .querySelectorAll("[data-variant-id][aria-disabled]")
                .forEach((node) => {
                  node.removeAttribute("aria-disabled");
                });
            }
          },

          /**
           * Handles both click and keyboard (Space/Enter) activation of a
           * swatch. `preventDefault` stops the native checkbox/label toggle
           * so `toggleSwatch` remains the single source of truth for
           * selection state.
           */
          onSwatchClick(event) {
            event.preventDefault();

            this.toggleSwatch(event.currentTarget);
          },

          /**
           * Adds or removes the swatch's variant from the selection,
           * depending on whether it's already selected.
           * @param {HTMLElement} target - The swatch element that was activated.
           */
          toggleSwatch(target) {
            const id = parseInt(target.dataset?.variantId, 10);
            const title = target.dataset?.title;

            if (!id) {
              return;
            }

            const found = this.items.find((item) => id === item.id);

            if (found?.loading) {
              return;
            }

            if (found) {
              this.removeSelection(found);
            } else {
              this.addSelection(id, title);
            }
          },

          /**
           * Optimistically adds a free-gift variant to the selection and the
           * cart, marking the swatch as loading/checked as the request
           * settles.
           * @param {number} id - The variant ID to add.
           * @param {string} [title] - The variant title, used for display
           *   before the cart response comes back.
           * @returns {Promise<void>}
           */
          async addSelection(id, title) {
            if (this.isMaxSelection) {
              return;
            }

            if (this.hasVariant(id)) {
              return;
            }

            const field = this.getField(id);

            if (!field) {
              return;
            }

            field.classList.add("is-loading");
            field.setAttribute("aria-busy", true);

            const item = {
              id,
              quantity: 1,
              properties: this.getFieldProperties(id),
              title,
              loading: true,
            };

            this.items.push(item);

            this.$ajaxCart.add(
              { items: [item] },
              {
                lastCallback: () => {
                  field.classList.remove("is-loading");
                  field.removeAttribute("aria-busy");

                  field.classList.add("is-checked");
                  field.setAttribute("aria-checked", true);

                  item.loading = false;
                },
              },
            );
          },

          /**
           * Removes a previously-selected free-gift variant from the cart
           * (setting its line item quantity to 0) and from the selection.
           * @param {{ id: number, key?: string }} found - The selected item to remove.
           */
          removeSelection(found) {
            if (!this.hasVariant(found.id)) {
              return;
            }

            const field = this.getField(found.id);

            if (!field) {
              return;
            }

            field.classList.add("is-loading");
            field.setAttribute("aria-busy", true);

            found.loading = true;

            this.$ajaxCart.change(
              { id: found.key || found.id, quantity: 0 },
              {
                lastCallback: () => {
                  field.classList.remove("is-loading");
                  field.removeAttribute("aria-busy");

                  field.classList.remove("is-checked");
                  field.setAttribute("aria-checked", false);

                  this.items = this.items.filter(
                    (item) => found.id !== item.id,
                  );
                },
              },
            );
          },

          /**
           * Reconciles `items` (and the checked/disabled state of the
           * swatch fields) with the free-gift line items currently in the
           * cart. Called on init and after every cart request so that
           * changes made outside this widget (cart drawer removals,
           * another tab, etc.) stay reflected here.
           */
          syncItems() {
            const cartItems = this.$ajaxCart.cart?.items || [];

            const freeGiftItems = cartItems.filter(
              (cartItem) => "true" === cartItem.properties?._free_gift,
            );

            this.items
              .filter(
                (item) =>
                  !freeGiftItems.some(
                    (cartItem) => cartItem.variant_id === item.id,
                  ),
              )
              .forEach((item) => {
                const field = this.getField(item.id);

                if (!field) {
                  return;
                }

                field.classList.remove("is-checked", "is-loading");
                field.setAttribute("aria-checked", false);
                field.removeAttribute("aria-busy");
              });

            this.items = freeGiftItems
              .map((cartItem) => {
                const field = this.getField(cartItem.variant_id);

                if (!field) {
                  return null;
                }

                field.classList.add("is-checked");
                field.setAttribute("aria-checked", true);

                return {
                  id: cartItem.variant_id,
                  key: cartItem.key,
                  quantity: cartItem.quantity,
                  properties: this.getFieldProperties(cartItem.variant_id),
                  title: cartItem.variant_title,
                  loading: false,
                };
              })
              .filter((item) => null !== item);

            this.updateDisabledSwatches();
          },

          /**
           * The root element is `data-ajax-cart-static-element`, so the
           * Liquid Ajax Cart section swap preserves this exact node instead
           * of replacing it — which keeps our Alpine state (`items`, etc.)
           * alive, but also means the server-rendered progress bar/messages
           * never reach the DOM on their own. Pull the fresh markup out of
           * the request's section HTML and morph it in by hand instead.
           * @param {object} [requestState] - The `liquid-ajax-cart` request state.
           */
          morphFromSections(requestState) {
            const sections = {
              ...requestState?.responseData?.body?.sections,
              ...requestState?.extraResponseData?.body?.sections,
            };

            for (const html of Object.values(sections)) {
              if ("string" !== typeof html) {
                continue;
              }

              const next = new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector("[x-cart-upsell-progress]");

              if (next) {
                this.$morph(this.$root, next);
                return;
              }
            }
          },

          /**
           * @param {number} id - The variant ID to look for.
           * @returns {boolean} Whether the variant is currently selected.
           */
          hasVariant(id) {
            return Boolean(this.items.find((item) => id === item.id));
          },

          /**
           * @param {number} id - The variant ID whose swatch to look up.
           * @returns {HTMLElement|null} The matching swatch element, if any.
           */
          getField(id) {
            return this.$root.querySelector(`[data-variant-id="${id}"]`);
          },

          /**
           * Builds the line item properties for a variant, always marking it
           * as a free gift and merging in any extra properties declared on
           * the swatch's hidden `properties` inputs. Matches on a `name`
           * prefix since `lead-time-properties.liquid` appends its own
           * `[_lead_time*]` suffix to each input's name.
           * @param {number} id - The variant ID to build properties for.
           * @returns {object} The line item properties.
           */
          getFieldProperties(id) {
            const props = {
              _free_gift: "true",
            };

            const fields = this.$root.querySelectorAll(
              `input[name^="cart_upsell_progress[${id}][properties]"]`,
            );

            fields.forEach((field) => {
              if (field.dataset.propertyKey) {
                props[field.dataset.propertyKey] = field.value;
              }
            });

            return props;
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "cart-upsell-progress",
      (el, { expression }, { evaluate }) => {
        const options = expression ? evaluate(expression) : {};
        root(Alpine, el, options);
      },
    );
  });
})();
